import { motion } from 'framer-motion';
import type { RoundResult } from '../../types/kotch';
import { CARD_DEFINITIONS } from '../../data/kotch/cards';
import { LANE_NAMES } from '../../data/kotch/gameConstants';

interface RoundResolutionProps {
  result: RoundResult;
  player1Name: string;
  player2Name: string;
  onContinue: () => void;
  isOnline?: boolean;
  isHost?: boolean;
}

export default function RoundResolution({
  result,
  player1Name,
  player2Name,
  onContinue,
  isOnline = false,
  isHost = false,
}: RoundResolutionProps) {
  const getWinnerName = () => {
    if (result.winnerId === 1) return player1Name;
    if (result.winnerId === 2) return player2Name;
    return 'Draw';
  };

  return (
    <motion.div
      className="kotch-results kotch-round-resolution"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h2>Round {result.roundNumber} Complete</h2>

      <div className="kotch-lane-results">
        {result.laneResults.map((laneResult, index) => {
          const p1Card = laneResult.player1Card
            ? CARD_DEFINITIONS[laneResult.player1Card.definitionId]
            : null;
          const p2Card = laneResult.player2Card
            ? CARD_DEFINITIONS[laneResult.player2Card.definitionId]
            : null;

          const outcomeClass = laneResult.winningPlayerId === 1
            ? 'win'
            : laneResult.winningPlayerId === 2
              ? 'lose'
              : 'draw';

          return (
            <motion.div
              key={laneResult.lane}
              className={`kotch-lane-result-block ${outcomeClass}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.15 }}
            >
              <span className="kotch-lane-result-lane">
                {LANE_NAMES[laneResult.lane]}
              </span>
              <div className="kotch-lane-result-cards">
                <span>{p1Card ? `${p1Card.type[0].toUpperCase()}${p1Card.power}` : '—'}</span>
                <span style={{ color: '#8a8a8a' }}>vs</span>
                <span>{p2Card ? `${p2Card.type[0].toUpperCase()}${p2Card.power}` : '—'}</span>
              </div>
              <span className={`kotch-lane-result-outcome ${outcomeClass}`}>
                {laneResult.reason}
              </span>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="kotch-result-row" style={{ maxWidth: 300 }}>
          <span className="kotch-result-label">Round Winner</span>
          <span className={`kotch-result-value ${result.winnerId === 1 ? 'win' : result.winnerId === 2 ? 'lose' : ''}`}>
            {getWinnerName()}
          </span>
        </div>
        <p style={{ color: '#8a8a8a', marginTop: '0.5rem' }}>
          {result.winReason}
        </p>
      </motion.div>

      {isOnline && !isHost ? (
        <p className="kotch-lobby-waiting-text">Waiting for host to continue...</p>
      ) : (
        <button className="kotch-button" onClick={onContinue}>
          Continue
        </button>
      )}
    </motion.div>
  );
}
